import React from "react";
import { Leaf } from "lucide-react";
import { useGetAIModels } from "../../../api/queryHooks";
import useModelPreferences from "../../../hooks/useModelPreferences";
import { modelStyleMap } from "../../../utils/modelColours";
import CustomTooltip from "../../Tooltip/CustomTooltip";

/**
 * ModelBadge - Displays a styled badge for AI model identification
 *
 * @param {string} model_id - The ID of the model
 * @param {string} modelDbName - Optional fallback model name from database
 * @param {number} currentIndex - Optional index for multi-answer scenarios
 * @param {Object} usage - Optional token usage { tokens_input, tokens_output, tokens_total, tokens_cached }
 */
const ModelBadge = ({ model_id, modelDbName, currentIndex, usage }) => {
  const { getEnabledModels } = useModelPreferences();
  const { data: allModels, isLoading, isError } = useGetAIModels();

  if (isLoading || isError) return null;
  if (!model_id) return null;

  const options = getEnabledModels(allModels);
  const model_name = options.find((model) => model.id === model_id)?.name || modelDbName;

  if (!model_name) return null;

  const allClasses = modelStyleMap[model_name] || "";
  const colorClass = allClasses.split(" ").filter((c) => c.startsWith("text-") || c.startsWith("dark:text-")).join(" ") || "text-ink-hex-7094f9";

  const formatTokens = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  const totalTokens = usage?.tokens_total;
  const cachedTokens = usage?.tokens_cached;

  const tooltipContent = totalTokens ? (
    <div className="text-[11px] space-y-0.5">
      <div>Prompt: {formatTokens(usage.tokens_input ?? 0)}</div>
      <div>Completion: {formatTokens(usage.tokens_output ?? 0)}</div>
      {cachedTokens ? <div className="text-ink-green-400">Cached: {formatTokens(cachedTokens)}</div> : null}
    </div>
  ) : null;

  const badge = (
    <span className={`photon-model-badge flex flex-col sm:flex-row items-end sm:items-center gap-0 sm:gap-1.5 text-[11px] font-medium ${colorClass}`}>
      {totalTokens ? (
        <span className="inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap">
          <span className="opacity-70 whitespace-nowrap">{formatTokens(totalTokens)}</span>
          {cachedTokens ? (
            <>
              <span className="opacity-40">·</span>
              <Leaf className="w-3 h-3 text-ink-green-500 dark:text-ink-green-400 shrink-0" />
              <span className="text-ink-green-500 dark:text-ink-green-400 whitespace-nowrap">{formatTokens(cachedTokens)}</span>
            </>
          ) : null}
          <span className="opacity-40 hidden sm:inline">·</span>
        </span>
      ) : null}
      <span className="truncate max-w-full">{model_name}</span>
    </span>
  );

  return tooltipContent ? (
    <CustomTooltip content={tooltipContent}>{badge}</CustomTooltip>
  ) : badge;
};

export default ModelBadge;
